import {
  useState,
  useEffect,
  Fragment,
  forwardRef,
  useImperativeHandle,
} from 'react'
import { useForm, Controller, FormContext } from 'react-hook-form'
import {
  ChevronDown as ChevronDownIcon,
  ChevronUp as ChevronUpIcon,
} from 'react-feather'
import { isEmpty, omitBy } from 'lodash'
import { Skeleton } from '@material-ui/lab'
import {
  Box,
  Collapse,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  Paper,
  Switch,
  Typography,
} from '@material-ui/core'

import { DaiControlHeader, SolutionController } from './components'

import { useDaiOptOut, useSnackbar } from 'hooks'

import constants from 'constants/index'

import useStyles from './styles'

const DaiControlSection = forwardRef((_, ref) => {
  const classes = useStyles()

  const [collapsedGroups, setCollapsedGroups] = useState({})

  const {
    daiOptOut,
    isLoading: isLoadingOptOut,
    refresh,
    manage,
  } = useDaiOptOut()
  const snackbar = useSnackbar()

  const { GROUP_MANAGEABLE, SOLUTION_MANAGEABLE, STATUSES } =
    constants.dai.solutions

  const groups = daiOptOut?.groups ?? []
  const solutions = daiOptOut?.solutions ?? []

  const solutionControls = [
    ...groups
      .filter((group) => group.manageable === GROUP_MANAGEABLE.PER_SOLUTION)
      .flatMap((group) =>
        (group.solutions ?? []).filter(
          (solution) => solution.manageable === SOLUTION_MANAGEABLE.INDIVIDUAL,
        ),
      ),
    ...solutions,
  ]

  const defaultValues = {
    global: daiOptOut?.global?.status ?? '',
    ...Object.fromEntries(
      groups.map((group) => [group.tag, group.status ?? '']),
    ),
    ...Object.fromEntries(
      solutionControls.map((solution) => [solution.tag, solution.status ?? '']),
    ),
  }

  const formMethods = useForm({ defaultValues })
  const { control, handleSubmit, errors, watch, reset } = formMethods

  useEffect(() => {
    if (!isEmpty(daiOptOut)) reset(defaultValues)
    // eslint-disable-next-line
  }, [daiOptOut])

  const allValues = watch()
  const isGlobalEnabled = allValues?.global !== STATUSES.DISABLED
  const hasItems = !isEmpty(groups) || !isEmpty(solutions)

  const handleStatusChange = (checked) =>
    checked ? STATUSES.ENABLED : STATUSES.DISABLED

  const pickByTag = (formData, items = []) =>
    omitBy(
      Object.fromEntries(items.map(({ tag }) => [tag, formData[tag]])),
      isEmpty,
    )

  const toggleCollapse = (group) =>
    setCollapsedGroups((prev) => ({ ...prev, [group.tag]: !prev[group.tag] }))

  const onSubmit = async (formData) => {
    try {
      await manage({
        ...(!isEmpty(formData.global) && { global: formData.global }),
        groups: pickByTag(formData, groups),
        solutions: pickByTag(formData, solutionControls),
      })
      snackbar.open({
        message: 'Configurações de IA atualizadas com sucesso!',
        variant: 'success',
      })
      refresh()
    } catch (error) {
      snackbar.open({
        message:
          error?.response?.data?.error?.message ??
          'Erro ao salvar configurações.',
        variant: 'error',
      })
    }
  }

  useImperativeHandle(ref, () => ({
    submit: handleSubmit(onSubmit),
  }))

  return (
    <FormContext {...formMethods}>
      <Paper>
        <DaiControlHeader isLoading={isLoadingOptOut} />
        <Collapse in={(isGlobalEnabled && hasItems) || isLoadingOptOut}>
          <Divider />
          <Box p={2}>
            {isLoadingOptOut ? (
              <Box className={classes.skeletonBox}>
                {Array.from({ length: 6 }).map((_, index) => (
                  <Skeleton
                    key={index}
                    height={24}
                    width="100%"
                    variant="rect"
                  />
                ))}
              </Box>
            ) : (
              <Fragment>
                {groups?.map((group, groupIndex) => {
                  const isGroupEnabled =
                    allValues[group.tag] !== STATUSES.DISABLED
                  const isPerSolution =
                    group.manageable === GROUP_MANAGEABLE.PER_SOLUTION
                  const isExpanded = !collapsedGroups[group.tag]

                  return (
                    <Box key={groupIndex}>
                      <Controller
                        as={
                          <FormControl fullWidth>
                            <FormControlLabel
                              label={
                                <Box className={classes.groupLabelBox}>
                                  <Typography variant="body1">
                                    {group.name}
                                  </Typography>
                                  {isPerSolution && isGroupEnabled && (
                                    <IconButton
                                      size="small"
                                      onClick={() => toggleCollapse(group)}
                                    >
                                      {isExpanded ? (
                                        <ChevronUpIcon size={18} />
                                      ) : (
                                        <ChevronDownIcon size={18} />
                                      )}
                                    </IconButton>
                                  )}
                                </Box>
                              }
                              labelPlacement="start"
                              className={classes.formControlLabel}
                              control={
                                <Switch
                                  checked={isGroupEnabled}
                                  color="primary"
                                />
                              }
                            />
                            {!!errors?.[group.tag] && (
                              <FormHelperText error>
                                {errors?.[group.tag]?.message}
                              </FormHelperText>
                            )}
                          </FormControl>
                        }
                        control={control}
                        name={group.tag}
                        onChange={([event]) =>
                          handleStatusChange(event.target.checked)
                        }
                      />
                      {isPerSolution && (
                        <Collapse
                          in={
                            isGroupEnabled &&
                            isExpanded &&
                            !isEmpty(group.solutions)
                          }
                        >
                          <Box pl={5}>
                            {group.solutions?.map((solution, solutionIndex) => (
                              <SolutionController
                                key={solutionIndex}
                                solution={solution}
                                groupFieldName={group.tag}
                              />
                            ))}
                          </Box>
                        </Collapse>
                      )}
                    </Box>
                  )
                })}
                {solutions.map((solution, solutionIndex) => (
                  <SolutionController key={solutionIndex} solution={solution} />
                ))}
              </Fragment>
            )}
          </Box>
        </Collapse>
      </Paper>
    </FormContext>
  )
})

DaiControlSection.displayName = 'DaiControlSection'

export default DaiControlSection
